import { Navigate, Route, useRoutes } from 'react-router-dom';

import { CONFIG } from 'src/config-global';

import { createTheme, useTheme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { lazy, useMemo } from 'react';
import { Admin, CustomRoutes, Resource, usePermissions } from 'react-admin';
import { AuthSplitLayout } from 'src/layouts/auth-split';
import { AdminLayout } from 'src/pages/admin/components/Layout';
import AppAuthProvider from 'src/pages/admin/providers/AppAuthProvider';
import AppDataProvider from 'src/pages/admin/providers/AppDataProvider';
import { queryClient } from 'src/pages/admin/providers/queryClient';
import FakeDataProvider from 'src/pages/admin/providers/FakeDataProvider';
import i18nProvider from 'src/pages/admin/providers/i18nProvider';
import resources from 'src/pages/admin/resources';
import { MainDashboard } from 'src/pages/admin/resources/dashboard';
import VaultApprovalsRedirect, {
  VAULT_APPROVALS_PATH,
} from 'src/pages/admin/resources/vault/operations/ApprovalsRedirect';
import VaultRootRedirect, {
  VAULT_ROOT_PATH,
} from 'src/pages/admin/resources/vault/dashboard/VaultRootRedirect';
import { SettingsDrawer } from '../../components/settings';
import { PermissionData } from 'src/pages/admin/entities/permission.entity';
import { StorageKey, XStorage } from 'src/pages/admin/providers/storage';

// ----------------------------------------------------------------------
const isMock = process.env.REACT_APP_MODE === 'mock';

export function AppRouter() {
  const { vars, ..._theme } = useTheme();

  const theme = deepmerge(_theme, {
    components: {
      RaDatagrid: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            borderRadius: 0,

            [`& .RaDatagrid-headerCell`]: {
              fontSize: 14,
              color: vars.palette.text.secondary,
              fontWeight: _theme.typography.fontWeightSemiBold,
              backgroundColor: vars.palette.background.neutral,
              padding: _theme.spacing(2),
            },
            [`& .RaDatagrid-headerCell:first-of-type`]: {
              borderRadius: 0,
            },
            [`& .RaDatagrid-headerCell:last-of-type`]: {
              borderRadius: 0,
            },
            [`& .RaDatagrid-expandIconCell`]: {
              paddingLeft: '16px',
            },
          },
        },
      },
      RaFilterFormInput: {
        styleOverrides: {
          root: {
            '& .RaFilterFormInput-spacer': {
              width: 0,
            },
          },
        },
      },
      RaFilterForm: {
        styleOverrides: {
          root: {
            padding: 0,
            '& .RaFilterForm-filterFormInput .MuiFormControl-root': {
              marginTop: 0,
            },
          },
        },
      },
      RaList: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            paddingRight: 0,
            width: '100%',
            '& .RaList-main': {
              width: '100%',
            },
            '& .RaList-content': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              borderRadius: 0,
            },
            '& .RaList-actions': {
              marginBottom: 2,
            },
            '& .RaFilterForm-filterFormInput': {
              marginTop: 0,
            },
          },
        },
      },
    },
  });
  const myTheme = createTheme(theme as any);

  return useRoutes([
    { path: '/auth/google/callback', element: <Jwt.GoogleCallbackPage /> },
    {
      path: '/*',
      element: (
        <Admin
          dashboard={MainDashboard}
          dataProvider={isMock ? FakeDataProvider : AppDataProvider}
          authProvider={AppAuthProvider}
          i18nProvider={i18nProvider}
          layout={AdminLayout}
          loginPage={<LoginPage />}
          theme={myTheme}
          queryClient={queryClient}
        >
          {resources.map((resource) => (
            <Resource key={resource.name} {...resource} />
          ))}
          <CustomRoutes>
            <Route path={VAULT_ROOT_PATH} element={<VaultRootRedirect />} />
            <Route path={VAULT_APPROVALS_PATH} element={<VaultApprovalsRedirect />} />
          </CustomRoutes>
        </Admin>
      ),
    },
    // No match
    { path: '*', element: <Navigate to={CONFIG.auth.redirectPath} replace /> },
  ]);
}

const Jwt = {
  SignInPage: lazy(() => import('src/pages/auth/jwt/sign-in')),
  GoogleCallbackPage: lazy(() => import('src/pages/auth/jwt/google-callback')),
};

function LoginPage(props: any) {
  return (
    <AuthSplitLayout sx={{ height: '100vh' }} section={{ title: 'Hi, Welcome back' }}>
      <Jwt.SignInPage />
      <SettingsDrawer hideFont />
    </AuthSplitLayout>
  );
}
